import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Button,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Flex,
} from "@chakra-ui/react";
import { ExternalLink, Eye } from "lucide-react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const Projects: React.FC = () => {
  const bg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.800", "white");
  const textColor = useColorModeValue("gray.600", "gray.300");
  const cardBg = useColorModeValue("white", "gray.700");
  const cardHoverBg = useColorModeValue("white", "gray.600");
  const smallCardBg = useColorModeValue("gray.50", "gray.700");
  const smallCardHoverBg = useColorModeValue("white", "gray.600");
  const smallCardBorderColor = useColorModeValue("gray.200", "gray.600");
  const smallCardHoverBorderColor = useColorModeValue("brand.300", "brand.400");

  const projects = [
    {
      title: "Membersstack",
      description:
        "Membersstack is a membership management and all-in-one tool that lets you collect membership dues, payment, manage members of your organisation, create campaigns, events and more.",
      image:
        "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800",
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "Chakra UI",
        "Redux Toolkit",
      ],
      liveUrl: "https://dashboard.membersstack.com/",
      githubUrl: "#",
      featured: true,
    },
    {
      title: "Workspace Manager",
      description:
        "Workspace Manager is a platform that provides software and hardware tools to co-working spaces, hotels, lounges & Cafes to help manage, market, and monetize their spaces as Virtual Offices and co-working spaces.",
      image:
        "https://images.pexels.com/photos/1181406/pexels-photo-1181406.jpeg?auto=compress&cs=tinysrgb&w=800",
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "Chakra UI",
        "Redux Toolkit",
        "Role Based Access Control (RBAC)",
        "Multi Tenancy Architecture",
      ],
      liveUrl: "https://workspacemanager.co",
      githubUrl: "#",
      featured: true,
    },
    {
      title: "MTIS Learn",
      description:
        "MTIS Learn is a Learning Management System (LMS) for students of Macdonald Trevan International Schools, providing access to learning resources, assessments, and progress tracking, while enabling parents to monitor their child's academic performance and school activities.",
      image:
        "https://images.pexels.com/photos/159844/cellular-education-classroom-159844.jpeg?auto=compress&cs=tinysrgb&w=800",
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "Chakra UI",
        "Redux Toolkit",
        "Role Based Access Control (RBAC)",
        "Learning Management System (LMS)",
        "EdTech",
      ],
      liveUrl: "https://www.mtislearn.com/",
      githubUrl: "#",
      featured: false,
    },
    {
      title: "Sanwo Escrow Platform",
      description:
        "A comprehensive escrow service system with three core products: Sanwo Classic, Sanwo Express, and Sanwo Travels, providing secure transaction management.",
      image:
        "https://images.pexels.com/photos/259027/pexels-photo-259027.jpeg?auto=compress&cs=tinysrgb&w=800",
      technologies: [
        "React",
        "Next.js",
        "TypeScript",
        "TailwindCSS",
        "Chakra UI",
        "Redux Toolkit",
        "FinTech",
      ],
      liveUrl: "https://sanwo.io",
      githubUrl: "#",
      featured: false,
    },
  ];

  return (
    <Box id="projects" py={20} bg={bg}>
      <Container maxW="7xl">
        <MotionBox
          textAlign="center"
          mb={16}
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Heading
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="bold"
            color={headingColor}
            mb={4}
          >
            Featured Projects
          </Heading>
          <Text fontSize="xl" color={textColor} maxW="3xl" mx="auto">
            A showcase of my recent work, demonstrating expertise in building
            scalable web applications, management systems, and user-centric
            platforms.
          </Text>
        </MotionBox>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={12} mb={12}>
          {projects
            .filter((project) => project.featured)
            .map((project, index) => (
              <MotionBox
                key={project.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <Flex
                  flexDir="column"
                  bg={cardBg}
                  h="full"
                  borderRadius="xl"
                  // overflow="hidden"
                  boxShadow="lg"
                  _hover={{
                    transform: "translateY(-8px)",
                    boxShadow: "2xl",
                    bg: cardHoverBg,
                  }}
                  transition="all 0.3s ease"
                >
                  <Box position="relative" overflow="hidden">
                    <Image
                      src={project.image}
                      alt={project.title}
                      h="250px"
                      w="full"
                      objectFit="cover"
                      transition="transform 0.3s ease"
                      _hover={{ transform: "scale(1.05)" }}
                    />
                    <Box
                      position="absolute"
                      top={4}
                      right={4}
                      bg="brand.500"
                      color="white"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      Featured
                    </Box>
                  </Box>

                  <Flex flexDir="column" gap={4} p={6} flex={1}>
                    <Heading size="lg" color={headingColor}>
                      {project.title}
                    </Heading>
                    <Text color={textColor} lineHeight="tall" flex={1}>
                      {project.description}
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {project.technologies.map((tech) => (
                        <Badge
                          key={tech}
                          colorScheme="brand"
                          variant="subtle"
                          fontSize="xs"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </HStack>
                    <HStack spacing={4} w="full" justify="space-between">
                      <Button
                        leftIcon={<Eye size={16} />}
                        variant="solid"
                        size="sm"
                        as="a"
                        href={project.liveUrl}
                        target="_blank"
                      >
                        Live Demo
                      </Button>
                      {/* <Button
                        leftIcon={<Github size={16} />}
                        variant="outline"
                        size="sm"
                        as="a"
                        href={project.githubUrl}
                        target="_blank"
                      >
                        Code
                      </Button> */}
                    </HStack>
                  </Flex>
                </Flex>
              </MotionBox>
            ))}
        </SimpleGrid>

        <MotionBox
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Heading size="lg" mb={8} color={textColor}>
            More Projects
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
            {projects
              .filter((project) => !project.featured)
              .map((project, index) => (
                <MotionBox
                  key={project.title}
                  p={6}
                  bg={smallCardBg}
                  borderRadius="lg"
                  border="1px"
                  borderColor={smallCardBorderColor}
                  _hover={{
                    borderColor: smallCardHoverBorderColor,
                    bg: smallCardHoverBg,
                    transform: "translateY(-4px)",
                    boxShadow: "lg",
                  }}
                  transition="all 0.3s ease"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  // transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <VStack align="start" spacing={4}>
                    <HStack justify="space-between" w="full">
                      <Heading size="md" color={headingColor}>
                        {project.title}
                      </Heading>
                      <HStack>
                        <Button
                          size="xs"
                          variant="ghost"
                          as="a"
                          href={project.liveUrl}
                          target="_blank"
                        >
                          <ExternalLink size={14} />
                        </Button>
                        {/* <Button
                          size="xs"
                          variant="ghost"
                          as="a"
                          href={project.githubUrl}
                          target="_blank"
                        >
                          <Github size={14} />
                        </Button> */}
                      </HStack>
                    </HStack>
                    <Text color={textColor} fontSize="sm">
                      {project.description}
                    </Text>
                    <HStack spacing={2} flexWrap="wrap">
                      {project.technologies.map((tech) => (
                        <Badge
                          key={tech}
                          colorScheme="gray"
                          variant="subtle"
                          fontSize="xs"
                        >
                          {tech}
                        </Badge>
                      ))}
                    </HStack>
                  </VStack>
                </MotionBox>
              ))}
          </SimpleGrid>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default Projects;
